/**
 * Email Audience Builder Types
 * HubSpot-inspired segment building for email campaigns
 */

// ============================================================================
// Filter Operators
// ============================================================================

export type TextOperator =
  | "equals"
  | "not_equals"
  | "contains"
  | "not_contains"
  | "starts_with"
  | "ends_with"
  | "is_known"
  | "is_unknown";

export type NumberOperator =
  | "equals"
  | "not_equals"
  | "greater_than"
  | "greater_than_or_equal"
  | "less_than"
  | "less_than_or_equal"
  | "between";

export type DateOperator =
  | "is"
  | "is_before"
  | "is_after"
  | "is_between"
  | "is_more_than_days_ago"
  | "is_less_than_days_ago"
  | "is_known"
  | "is_unknown";

export type SelectOperator = "is_any_of" | "is_none_of" | "is_known" | "is_unknown";

export type BooleanOperator = "is_true" | "is_false" | "is_known" | "is_unknown";

export type FilterOperator =
  | TextOperator
  | NumberOperator
  | DateOperator
  | SelectOperator
  | BooleanOperator;

// ============================================================================
// Filter Field Types
// ============================================================================

export type FieldType = "text" | "number" | "date" | "select" | "multi_select" | "boolean";

export interface FilterFieldOption {
  id: string | number;
  label: string;
  icon?: string;
}

export interface FilterFieldDefinition {
  id: string;
  label: string;
  type: FieldType;
  category: FilterCategory;
  operators: FilterOperator[];
  options?: FilterFieldOption[]; // For select/multi_select fields
  placeholder?: string;
  description?: string;
}

// ============================================================================
// Filter Categories
// ============================================================================

export type FilterCategory =
  | "contact"
  | "company"
  | "event"
  | "learning"
  | "performance"
  | "financial"
  | "email_history"
  | "subscription";

export interface FilterCategoryDefinition {
  id: FilterCategory;
  label: string;
  icon: string;
  description: string;
  fields: FilterFieldDefinition[];
}

// ============================================================================
// Filter Values
// ============================================================================

export type FilterValue =
  | string
  | number
  | boolean
  | string[]
  | number[]
  | { from: string | number; to: string | number }
  | null;

// ============================================================================
// Filter Structure
// ============================================================================

export interface AudienceFilter {
  id: string;
  fieldId: string;
  operator: FilterOperator;
  value: FilterValue;
}

export type FilterGroupLogic = "AND" | "OR";

export interface FilterGroup {
  id: string;
  logic: FilterGroupLogic;
  filters: AudienceFilter[];
}

// ============================================================================
// Audience Definition
// ============================================================================

export type AudienceType = "active" | "static";

export interface AudienceCriteria {
  type: AudienceType;
  groupLogic: FilterGroupLogic; // Logic between groups
  filterGroups: FilterGroup[];
  excludeEmails?: string[];
}

export interface AudienceBreakdown {
  field: string;
  label: string;
  segments: {
    label: string;
    count: number;
    percentage: number;
  }[];
}

export interface AudiencePreviewData {
  totalCount: number;
  isProcessing: boolean;
  breakdown: AudienceBreakdown[];
  sampleContacts: {
    id: string;
    name: string;
    email: string;
    company?: string;
    avatar?: string;
  }[];
}

export interface SavedAudience {
  id: string;
  name: string;
  description?: string;
  type: AudienceType;
  criteria: AudienceCriteria;
  contactCount: number;
  createdAt: string;
  updatedAt: string;
  createdBy: {
    id: string;
    name: string;
  };
}

// ============================================================================
// UI State
// ============================================================================

export interface AudienceBuilderState {
  name: string;
  type: AudienceType;
  criteria: AudienceCriteria;
  preview: AudiencePreviewData | null;
  isPreviewLoading: boolean;
  isDirty: boolean;
  errors: Record<string, string>;
}

// ============================================================================
// Operator Labels (for UI)
// ============================================================================

export const OPERATOR_LABELS: Record<FilterOperator, string> = {
  // Text
  equals: "is equal to",
  not_equals: "is not equal to",
  contains: "contains",
  not_contains: "doesn't contain",
  starts_with: "starts with",
  ends_with: "ends with",
  // Number
  greater_than: "is greater than",
  greater_than_or_equal: "is at least",
  less_than: "is less than",
  less_than_or_equal: "is at most",
  between: "is between",
  // Date
  is: "is",
  is_before: "is before",
  is_after: "is after",
  is_between: "is between",
  is_more_than_days_ago: "is more than",
  is_less_than_days_ago: "is less than",
  // Select
  is_any_of: "is any of",
  is_none_of: "is none of",
  // Boolean
  is_true: "is true",
  is_false: "is false",
  // Universal
  is_known: "is known",
  is_unknown: "is unknown",
};

// ============================================================================
// Date Presets
// ============================================================================

export const DATE_PRESETS = [
  { id: "today", label: "Today" },
  { id: "yesterday", label: "Yesterday" },
  { id: "last_7_days", label: "Last 7 days" },
  { id: "last_30_days", label: "Last 30 days" },
  { id: "last_90_days", label: "Last 90 days" },
  { id: "this_month", label: "This month" },
  { id: "last_month", label: "Last month" },
  { id: "this_quarter", label: "This quarter" },
  { id: "this_year", label: "This year" },
] as const;
